import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { Landmark, BookOpen, FileText, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Actions as IMPORT_ACTIONS } from '../../store/actions/intelligentImportActions';
import { SOURCE_KINDS } from '../../constants/importConstants';

const sourceOptions = [
  {
    kind: SOURCE_KINDS.BANK,
    icon: Landmark,
    title: 'Bank Statement',
    description: 'Transaction list from a bank account export with dates, descriptions, and amounts',
  },
  {
    kind: SOURCE_KINDS.LEDGER,
    icon: BookOpen,
    title: 'Ledger / Accounting Export',
    description: 'Chart of accounts export from accounting software with account codes and categories',
  },
  {
    kind: SOURCE_KINDS.SUPPLIER,
    icon: FileText,
    title: 'Supplier Invoice',
    description: 'Invoice or bill from a supplier with line items, quantities, and unit information',
  },
];

const ClassificationResolver = ({ importId, job, setClassification, continueImport, loading }) => {
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await setClassification(importId, selected);
      await continueImport(importId, selected);
    } finally {
      setSubmitting(false);
    }
  }, [selected, importId, setClassification, continueImport]);

  return (
    <div className='flex flex-col gap-4'>
      <Alert>
        <AlertCircle size={16} />
        <AlertDescription>
          We couldn&apos;t automatically determine the file type. Please select the best match below.
        </AlertDescription>
      </Alert>

      {job?.sampleRows?.length > 0 && (
        <Card className='bg-card'>
          <CardHeader>
            <CardTitle className='text-sm'>Sample Data (first rows)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='overflow-x-auto'>
              <table className='text-xs font-mono'>
                <tbody>
                  {job.sampleRows.slice(0, 3).map((row, i) => (
                    <tr key={i}>
                      {(Array.isArray(row) ? row : [row]).map((cell, j) => (
                        <td key={j} className='pr-4 py-1 text-muted-foreground'>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        {sourceOptions.map((opt) => {
          const Icon = opt.icon;
          const isSelected = selected === opt.kind;
          return (
            <Card
              key={opt.kind}
              className={`cursor-pointer transition-all ${
                isSelected
                  ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                  : 'hover:border-primary/50'
              }`}
              onClick={() => setSelected(opt.kind)}
            >
              <CardContent className='p-6 text-center'>
                <Icon
                  size={32}
                  className={`mx-auto mb-3 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}
                  strokeWidth={1.5}
                />
                <h3 className='font-semibold text-foreground'>{opt.title}</h3>
                <p className='text-sm text-muted-foreground mt-1'>{opt.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className='flex justify-end'>
        <Button onClick={handleSubmit} disabled={!selected || submitting} loading={submitting}>
          Continue with Selection
        </Button>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  loading: state.intelligentImport?.loading,
});

const mapDispatchToProps = {
  setClassification: IMPORT_ACTIONS.setClassification,
  continueImport: IMPORT_ACTIONS.continueImport,
};

export default connect(mapStateToProps, mapDispatchToProps)(ClassificationResolver);
